import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers, CheckCircle, Clock, AlertCircle, Timer, MapPin,
  ChevronDown, ChevronRight, RefreshCw, Filter, RotateCcw, X,
  Search, Download, ExternalLink
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { lifecycleAPI } from '../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const COLORS = ['#0D9488', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981', '#EC4899', '#6366F1'];

const KpiCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-lg transition-shadow" data-testid={`offb-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`p-2.5 rounded-xl ${color}`}><Icon size={20} className="text-white" /></div>
      <div>
        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      </div>
    </div>
  </div>
);

const ChartBox = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-50">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      {subtitle && <p className="text-[11px] text-gray-500">{subtitle}</p>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Section = ({ id, title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`offb-section-${id}`}>
      <button onClick={() => setOpen(p => !p)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors">
        <Icon size={20} className="text-orange-500 flex-shrink-0" />
        <span className="font-semibold text-gray-800 flex-1">{title}</span>
        {open ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-gray-50">{children}</div>}
    </div>
  );
};

// ─── Offboarded Filter Components ──────────────────────────
const OffbMultiSelect = ({ label, options, selected, onChange, searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = React.useRef(null);
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const filtered = options.filter(o => !q || o.toLowerCase().includes(q.toLowerCase()));
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-xs border border-gray-200 rounded-lg hover:border-gray-300 bg-white" data-testid={`offb-filter-${label}`}>
        <span className="truncate text-gray-700">{selected.length > 0 ? `${label} (${selected.length})` : label}</span>
        <ChevronDown size={13} className="text-gray-400 ml-1 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <input type="text" placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-teal-400 outline-none" />
            </div>
          )}
          <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No options</p>}
            {filtered.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 truncate">{opt}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && <div className="p-2 border-t border-gray-100"><button onClick={() => onChange([])} className="text-[10px] text-red-500 hover:underline">Clear</button></div>}
        </div>
      )}
    </div>
  );
};

const EMPTY_OFFB_FILTERS = { reason: [], status: [], city: [], date_start: '', date_end: '' };

// Drilldown Panel for Offboarded
const OffbDrilldownPanel = ({ isOpen, data, title, subtitle, onClose, onFullView, loading }) => {
  const [search, setSearch] = useState('');
  if (!isOpen) return null;
  const filtered = (data || []).filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.hotelName || l.propertyName || '').toLowerCase().includes(q) ||
           (l.city || '').toLowerCase().includes(q) ||
           (l.offboardingReason || l.reason || '').toLowerCase().includes(q);
  });
  const handleExport = () => {
    const rows = filtered.map(item => ({
      'Property': item.hotelName || item.propertyName || '-',
      'City': item.city || '-',
      'Reason': item.offboardingReason || item.reason || '-',
      'Status': item.lifecycleStatus || '-',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Offboarded');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'offboarded_drilldown.xlsx');
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="offb-drilldown-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-800 text-base">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="offb-drilldown-export"><Download size={13} /> Export Excel</button>
            {onFullView && <button onClick={onFullView}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              data-testid="offb-drilldown-full-view"><ExternalLink size={13} /> Open Full View</button>}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg" data-testid="offb-drilldown-close"><X size={18} className="text-gray-500" /></button>
          </div>
        </div>
        <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-orange-500 outline-none"
              data-testid="offb-drilldown-search" />
          </div>
          <p className="text-xs text-gray-400 mt-1.5">{filtered.length} of {(data || []).length} results</p>
        </div>
        <div className="flex-1 overflow-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400"><Search size={32} className="mx-auto mb-2 text-gray-300" /><p>No matching records</p></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-12">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Property</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">City</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((item, i) => (
                  <tr key={item.hotel_id || i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-gray-400">{i + 1}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{item.hotelName || item.propertyName || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{item.city || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{item.offboardingReason || item.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const OffboardedAnalytics = ({ crossFilters = {}, onCrossFilterToggle }) => {
  const navigate = useNavigate();
  const [lifecycle, setLifecycle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ ...EMPTY_OFFB_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_OFFB_FILTERS });
  const [offbDd, setOffbDd] = useState({ open: false, data: [], title: '', subtitle: '', loading: false });

  const setFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

  const activeFilterCount = [
    ...(appliedFilters.reason || []),
    ...(appliedFilters.status || []),
    ...(appliedFilters.city || []),
    appliedFilters.date_start,
    appliedFilters.date_end,
  ].filter(Boolean).length;

  const applyFilters = useCallback(() => setAppliedFilters({ ...filters }), [filters]);
  const resetFilters = useCallback(() => {
    const empty = { ...EMPTY_OFFB_FILTERS };
    setFilters(empty);
    setAppliedFilters(empty);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await lifecycleAPI.getAll();
      setLifecycle(res.data || []);
    } catch (err) {
      console.error('Failed to load lifecycle data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const analytics = useMemo(() => {
    let offboardRequests = lifecycle.filter(l => (l.lifecycleStatus || l.propertyStatus || '').toLowerCase().includes('offboard'));

    // Merge cross-filters with local appliedFilters
    const mergedReason = [...new Set([...(appliedFilters.reason || []), ...(crossFilters.offboarded_reason || [])])];
    const mergedStatus = [...new Set([...(appliedFilters.status || []), ...(crossFilters.offboarded_status || [])])];
    const mergedCity = [...new Set([...(appliedFilters.city || []), ...(crossFilters.city || [])])];
    const mergedState = crossFilters.state || [];
    const dateStart = appliedFilters.date_start || crossFilters.date_range?.start || '';
    const dateEnd = appliedFilters.date_end || crossFilters.date_range?.end || '';

    // Apply merged filters
    if (mergedReason.length > 0) {
      offboardRequests = offboardRequests.filter(l => {
        const reason = l.offboardingReason || l.reason || 'Other';
        return mergedReason.includes(reason);
      });
    }
    if (mergedStatus.length > 0) {
      offboardRequests = offboardRequests.filter(l => {
        const status = (l.lifecycleStatus || '').toLowerCase();
        if (mergedStatus.includes('Completed') && status === 'offboarded') return true;
        if (mergedStatus.includes('In Progress') && status.includes('offboarding') && status !== 'offboarded') return true;
        return false;
      });
    }
    if (mergedCity.length > 0) {
      offboardRequests = offboardRequests.filter(l => mergedCity.includes(l.city || 'Unknown'));
    }
    if (mergedState.length > 0) {
      offboardRequests = offboardRequests.filter(l => mergedState.includes(l.state || 'Unknown'));
    }
    if (dateStart) {
      const start = new Date(dateStart);
      offboardRequests = offboardRequests.filter(l => {
        const d = new Date(l.createdAt || l.offboardingStartDate);
        return !isNaN(d) && d >= start;
      });
    }
    if (dateEnd) {
      const end = new Date(dateEnd + 'T23:59:59');
      offboardRequests = offboardRequests.filter(l => {
        const d = new Date(l.createdAt || l.offboardingStartDate);
        return !isNaN(d) && d <= end;
      });
    }

    const completed = offboardRequests.filter(l => (l.lifecycleStatus || '').toLowerCase() === 'offboarded').length;
    const inProgress = offboardRequests.filter(l => (l.lifecycleStatus || '').toLowerCase().includes('offboarding')).length;

    const reasonCounts = {};
    offboardRequests.forEach(l => {
      const reason = l.offboardingReason || l.reason || 'Other';
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });
    const defaultReasons = ['High Commission', 'Owner Closed Property', 'Owner Left OTA', 'Low Bookings', 'Other'];
    let reasonsPieData = defaultReasons.map((reason, i) => ({
      name: reason, value: reasonCounts[reason] || 0, color: COLORS[i % COLORS.length]
    })).filter(d => d.value > 0);
    if (reasonsPieData.length === 0 && offboardRequests.length > 0) {
      reasonsPieData = [{ name: 'Other', value: offboardRequests.length, color: COLORS[4] }];
    }

    const offboardingTimes = [];
    offboardRequests.forEach(l => {
      const requestDate = new Date(l.offboardingStartDate || l.createdAt);
      const completionDate = new Date(l.offboardingCompletedDate || l.updatedAt);
      if (!isNaN(requestDate) && !isNaN(completionDate) && completionDate > requestDate) {
        const days = Math.round((completionDate - requestDate) / (1000 * 60 * 60 * 24));
        if (days > 0 && days < 365) offboardingTimes.push(days);
      }
    });
    const avgTime = offboardingTimes.length > 0 ? Math.round(offboardingTimes.reduce((a, b) => a + b, 0) / offboardingTimes.length) : 0;
    const maxTime = offboardingTimes.length > 0 ? Math.max(...offboardingTimes) : 0;
    const minTime = offboardingTimes.length > 0 ? Math.min(...offboardingTimes) : 0;

    const timeTrend = {};
    offboardRequests.forEach(l => {
      const date = new Date(l.createdAt || l.offboardingStartDate);
      if (!isNaN(date)) {
        const mk = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!timeTrend[mk]) timeTrend[mk] = [];
        const cd = new Date(l.offboardingCompletedDate || l.updatedAt);
        if (!isNaN(cd) && cd > date) {
          const days = Math.round((cd - date) / (1000 * 60 * 60 * 24));
          if (days > 0 && days < 365) timeTrend[mk].push(days);
        }
      }
    });
    const offboardingTimeTrend = Object.entries(timeTrend)
      .sort(([a], [b]) => a.localeCompare(b)).slice(-12)
      .map(([month, times]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        avgDays: times.length > 0 ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : 0
      }));

    const cityData = {};
    offboardRequests.forEach(l => { const c = l.city || 'Unknown'; cityData[c] = (cityData[c] || 0) + 1; });
    const offboardByCity = Object.entries(cityData).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 10);

    const statusPie = [
      { name: 'Completed', value: completed, color: '#10B981' },
      { name: 'In Progress', value: inProgress, color: '#F59E0B' }
    ].filter(d => d.value > 0);

    return { total: offboardRequests.length, completed, inProgress, statusPie, offboardByCity, reasonsPieData, avgTime, maxTime, minTime, offboardingTimeTrend, _filteredRecords: offboardRequests };
  }, [lifecycle, appliedFilters, crossFilters]);

  // Extract filter options from raw lifecycle data
  const filterOptions = useMemo(() => {
    const allOffboard = lifecycle.filter(l => (l.lifecycleStatus || l.propertyStatus || '').toLowerCase().includes('offboard'));
    const reasons = [...new Set(allOffboard.map(l => l.offboardingReason || l.reason || 'Other'))].sort();
    const cities = [...new Set(allOffboard.map(l => l.city || 'Unknown'))].sort();
    return { reason: reasons, status: ['Completed', 'In Progress'], city: cities };
  }, [lifecycle]);

  // Drilldown handler: filter from the same base dataset
  const openOffbDrilldown = useCallback((filterType, filterValue, label) => {
    const records = analytics._filteredRecords || [];
    let ddData = [];
    if (filterType === 'reason') {
      ddData = records.filter(l => (l.offboardingReason || l.reason || 'Other') === filterValue);
    } else if (filterType === 'status') {
      const status = (filterValue || '').toLowerCase();
      if (status === 'completed') ddData = records.filter(l => (l.lifecycleStatus || '').toLowerCase() === 'offboarded');
      else ddData = records.filter(l => (l.lifecycleStatus || '').toLowerCase().includes('offboarding') && (l.lifecycleStatus || '').toLowerCase() !== 'offboarded');
    } else if (filterType === 'city') {
      ddData = records.filter(l => (l.city || 'Unknown') === filterValue);
    }
    setOffbDd({ open: true, data: ddData, title: `${label}: ${filterValue}`, subtitle: `${ddData.length} properties`, loading: false });
  }, [analytics]);

  const closeOffbDd = () => setOffbDd({ open: false, data: [], title: '', subtitle: '', loading: false });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-60" data-testid="offb-loading">
        <div className="text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto mb-3" /><p className="text-gray-500 text-sm">Loading offboarded analytics...</p></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="offboarded-analytics">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="offb-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="offb-filter-bar">
        <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
          <OffbMultiSelect label="Reason" options={filterOptions.reason} selected={filters.reason || []} onChange={v => setFilter('reason', v)} />
          <OffbMultiSelect label="Status" options={filterOptions.status} selected={filters.status || []} onChange={v => setFilter('status', v)} />
          <OffbMultiSelect label="City" options={filterOptions.city} selected={filters.city || []} onChange={v => setFilter('city', v)} searchable />
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium text-gray-500 whitespace-nowrap">Date Range</span>
            <input type="date" value={filters.date_start || ''} onChange={e => setFilter('date_start', e.target.value)}
              className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-orange-400 outline-none" data-testid="offb-filter-date-start" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={filters.date_end || ''} onChange={e => setFilter('date_end', e.target.value)}
              className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-orange-400 outline-none" data-testid="offb-filter-date-end" />
          </div>
          <button onClick={applyFilters} className="px-3 py-2 text-xs font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors" data-testid="offb-filter-apply">
            <Filter size={13} className="inline mr-1" />Apply
          </button>
          {activeFilterCount > 0 && (
            <button onClick={resetFilters} className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-red-500 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors" data-testid="offb-filter-reset">
              <RotateCcw size={12} /> Reset
            </button>
          )}
        </div>
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-1.5 px-4 pb-3">
            {(appliedFilters.reason || []).map(v => (
              <span key={`r-${v}`} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-orange-50 text-orange-700 rounded-md">
                Reason: {v} <button onClick={() => { setFilter('reason', (filters.reason || []).filter(x => x !== v)); setAppliedFilters(prev => ({ ...prev, reason: (prev.reason || []).filter(x => x !== v) })); }}><X size={11} /></button>
              </span>
            ))}
            {(appliedFilters.status || []).map(v => (
              <span key={`st-${v}`} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 rounded-md">
                Status: {v} <button onClick={() => { setFilter('status', (filters.status || []).filter(x => x !== v)); setAppliedFilters(prev => ({ ...prev, status: (prev.status || []).filter(x => x !== v) })); }}><X size={11} /></button>
              </span>
            ))}
            {(appliedFilters.city || []).map(v => (
              <span key={`ct-${v}`} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-violet-50 text-violet-700 rounded-md">
                City: {v} <button onClick={() => { setFilter('city', (filters.city || []).filter(x => x !== v)); setAppliedFilters(prev => ({ ...prev, city: (prev.city || []).filter(x => x !== v) })); }}><X size={11} /></button>
              </span>
            ))}
            {appliedFilters.date_start && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-blue-50 text-blue-700 rounded-md">
                From: {appliedFilters.date_start} <button onClick={() => { setFilter('date_start', ''); setAppliedFilters(prev => ({ ...prev, date_start: '' })); }}><X size={11} /></button>
              </span>
            )}
            {appliedFilters.date_end && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-blue-50 text-blue-700 rounded-md">
                To: {appliedFilters.date_end} <button onClick={() => { setFilter('date_end', ''); setAppliedFilters(prev => ({ ...prev, date_end: '' })); }}><X size={11} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="offb-kpi-cards">
        <KpiCard title="Total Requests" value={analytics.total} icon={Layers} color="bg-gray-500" />
        <KpiCard title="Completed" value={analytics.completed} icon={CheckCircle} color="bg-emerald-600" />
        <KpiCard title="In Progress" value={analytics.inProgress} icon={Clock} color="bg-orange-500" />
      </div>

      {/* Reasons & Time */}
      <Section id="reasons-time" title="Offboarding Reasons & Time" icon={Timer} defaultOpen={true}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
          <ChartBox title="Offboarding Reasons" subtitle="Why properties are offboarded">
            {analytics.reasonsPieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart><Pie data={analytics.reasonsPieData} cx="50%" cy="50%" outerRadius={70} dataKey="value"
                    label={({ name, value }) => `${name} (${value})`}
                    onClick={(_, idx) => {
                      const item = analytics.reasonsPieData[idx];
                      if (item) {
                        if (onCrossFilterToggle) onCrossFilterToggle('offboarded_reason', item.name);
                        openOffbDrilldown('reason', item.name, 'Reason');
                      }
                    }}
                    style={{ cursor: 'pointer' }}>
                    {analytics.reasonsPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie><Tooltip formatter={(v, n) => [`${v} properties`, n]} /></PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 justify-center mt-2">
                  {analytics.reasonsPieData.map((d, i) => (
                    <button key={i} onClick={() => {
                      if (onCrossFilterToggle) onCrossFilterToggle('offboarded_reason', d.name);
                      openOffbDrilldown('reason', d.name, 'Reason');
                    }}
                      className="flex items-center gap-1 text-xs hover:opacity-70 transition-opacity cursor-pointer">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-gray-600">{d.name}: {d.value}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm"><AlertCircle size={20} className="mr-2" />No offboarding data</div>
            )}
          </ChartBox>

          <ChartBox title="Offboarding Time Analytics" subtitle="Time to complete offboarding">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <Timer size={20} className="mx-auto text-blue-600 mb-1" />
                <p className="text-2xl font-bold text-gray-900">{analytics.avgTime}</p>
                <p className="text-xs text-gray-500">Avg Days</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-gray-900">{analytics.minTime}</p>
                <p className="text-xs text-gray-500">Min Days</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-gray-900">{analytics.maxTime}</p>
                <p className="text-xs text-gray-500">Max Days</p>
              </div>
            </div>
            {analytics.offboardingTimeTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={analytics.offboardingTimeTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="avgDays" name="Avg Days" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 text-sm py-4">No trend data</p>}
          </ChartBox>
        </div>
      </Section>

      {/* Status & City */}
      <Section id="status-city" title="Status & City Breakdown" icon={MapPin}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
          <ChartBox title="Offboard Status" subtitle="Completion breakdown">
            {analytics.statusPie.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart><Pie data={analytics.statusPie} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  onClick={(_, idx) => {
                    const item = analytics.statusPie[idx];
                    if (item) {
                      if (onCrossFilterToggle) onCrossFilterToggle('offboarded_status', item.name);
                      openOffbDrilldown('status', item.name, 'Status');
                    }
                  }}
                  style={{ cursor: 'pointer' }}>
                  {analytics.statusPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 text-sm py-8">No status data</p>}
          </ChartBox>

          <ChartBox title="Offboards by City" subtitle="Top 10">
            {analytics.offboardByCity.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={analytics.offboardByCity} layout="vertical"
                  onClick={(e) => {
                    if (e?.activePayload?.[0]?.payload?.name) {
                      const name = e.activePayload[0].payload.name;
                      if (onCrossFilterToggle) onCrossFilterToggle('city', name);
                      openOffbDrilldown('city', name, 'City');
                    }
                  }}
                  style={{ cursor: 'pointer' }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8B5CF6" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-gray-400 text-sm py-8">No city data</p>}
          </ChartBox>
        </div>
      </Section>

      {/* Drilldown Panel */}
      <OffbDrilldownPanel
        isOpen={offbDd.open}
        data={offbDd.data}
        title={offbDd.title}
        subtitle={offbDd.subtitle}
        loading={offbDd.loading}
        onClose={closeOffbDd}
      />
    </div>
  );
};

export default OffboardedAnalytics;
